/**
 * Simplified cook time calculator for frontend
 * Ported from backend calculator service
 */

import type { SmokerSpec, MeatProperties, CookDetails, CookEstimate, WeatherData, CalculationConstants } from '../types';

export class CookTimeCalculator {
  /**
   * Calculate cook time estimate
   */
  calculate(
    cookDetails: CookDetails,
    smokerSpec: SmokerSpec,
    meatProps: MeatProperties,
    weather: WeatherData,
    constants: CalculationConstants
  ): CookEstimate {
    // Filter out zero-weight pieces
    const validPieces = cookDetails.pieces.filter(p => p.weight > 0);
    
    if (validPieces.length === 0) {
      throw new Error('No pieces with weight > 0');
    }

    // Find largest piece
    const largestPiece = Math.max(...validPieces.map(p => p.weight));
    const numPieces = validPieces.length;

    // Base cook rate (hrs/lb at target temperature)
    // Base time calculation
    let baseTime: number;
    let baseCookRate: number;
    
    // Special handling for meats that cook by thickness/geometry (e.g., beef ribs)
    // These meats cook in parallel based on the thickest piece, not total mass
    if (meatProps.empirical.useFixedCookTime) {
      // Use fixed cook time based on temperature, ignore weight
      const temp = cookDetails.smokerTempF;
      if (temp <= constants.temperatureBreakpoints.fixedCookTime.at225F && meatProps.empirical.fixedBaseTimeAt225F) {
        baseTime = meatProps.empirical.fixedBaseTimeAt225F;
      } else if (temp <= constants.temperatureBreakpoints.fixedCookTime.at250F && meatProps.empirical.fixedBaseTimeAt250F) {
        baseTime = meatProps.empirical.fixedBaseTimeAt250F;
      } else if (meatProps.empirical.fixedBaseTimeAt275F) {
        baseTime = meatProps.empirical.fixedBaseTimeAt275F;
      } else {
        // Fallback to 250F time if available
        baseTime = meatProps.empirical.fixedBaseTimeAt250F || 9.0;
      }
      // Derive a synthetic cook rate for per-piece calculations
      // Use typical weight to maintain consistent per-piece behavior
      const typicalWeight = 3.0; // Typical plate rib weight
      baseCookRate = baseTime / typicalWeight;
    } else {
      // Standard weight-based calculation
      baseCookRate = this.getBaseCookRate(meatProps, cookDetails.smokerTempF);
      baseTime = largestPiece * baseCookRate;
    }

    // Apply spacing and thermal load factors
    // SPACING IS THE DOMINANT VARIABLE for multi-piece cooks!
    const spacingFactor = cookDetails.spacing?.spacingFactor || (numPieces > 1 ? 1.1 : 1.0);
    const thermalLoadFactorPerPiece = meatProps.empirical.thermalLoadFactorPerPiece || 0.03;
    const thermalLoadFactor = 1 + (Math.min(numPieces, 6) * thermalLoadFactorPerPiece);

    let cookTime = baseTime * spacingFactor * thermalLoadFactor;

    // Target temperature adjustment - adjust cook time based on user's target vs standard target
    const standardTargetTemp = meatProps.temperatures.targetInternalF;
    const userTargetTemp = cookDetails.targetTempF;
    const startTemp = meatProps.temperatures.startTempF;
    
    if (userTargetTemp !== standardTargetTemp) {
      // Calculate temperature rise ratios
      const standardRise = standardTargetTemp - startTemp; // e.g., 203 - 38 = 165°F
      const userRise = userTargetTemp - startTemp;         // e.g., 195 - 38 = 157°F
      const targetTempFactor = userRise / standardRise;    // e.g., 157/165 = 0.952
      
      cookTime *= targetTempFactor;
    }

    // Calculate preliminary timeline to get cook window for weather
    const preliminaryTotalTime = cookTime + cookDetails.restHours;
    const preliminaryTargetDate = cookDetails.targetServingTime 
      ? new Date(cookDetails.targetServingTime)
      : new Date(Date.now() + 24 * 60 * 60 * 1000);
    const preliminaryStartTime = new Date(preliminaryTargetDate.getTime() - preliminaryTotalTime * 60 * 60 * 1000);

    // Weather adjustment (now with cook window awareness)
    const weatherFactor = this.calculateWeatherImpact(
      weather, 
      smokerSpec, 
      cookDetails,
      preliminaryStartTime,
      preliminaryTargetDate
    );
    cookTime *= weatherFactor;

    // Wrapping adjustment
    if (cookDetails.wrapping?.enabled && meatProps.wrappingTimeSavingsPercent) {
      const wrapType = cookDetails.wrapping.wrapType || 'foil';
      const wrapTemp = cookDetails.wrapping.atTempF;
      
      // Foil: full savings, Butcher paper: 70% of savings (still allows some moisture escape)
      const wrapMaterialFactor = wrapType === 'foil' ? 1.0 : 0.7;
      
      // Timing factor - adjust savings based on when they wrap
      let timingFactor = 1.0;
      if (wrapTemp && meatProps.temperatures.stallStartF && meatProps.temperatures.stallEndF) {
        const stallStart = meatProps.temperatures.stallStartF;
        const stallEnd = meatProps.temperatures.stallEndF;
        const stallMid = (stallStart + stallEnd) / 2;
        
        if (wrapTemp < stallStart) {
          // Wrapping before stall - slightly less effective (meat needs some bark development)
          timingFactor = 0.85;
        } else if (wrapTemp <= stallMid) {
          // Wrapping early in stall - maximum savings
          timingFactor = 1.1;
        } else if (wrapTemp <= stallEnd) {
          // Wrapping mid-to-late stall - good savings (optimal)
          timingFactor = 1.0;
        } else {
          // Wrapping after stall - reduced savings
          const tempsPastStall = wrapTemp - stallEnd;
          timingFactor = Math.max(0.5, 1.0 - (tempsPastStall / 40)); // Diminishing returns
        }
      }
      
      const savings = (meatProps.wrappingTimeSavingsPercent / 100) * wrapMaterialFactor * timingFactor;
      cookTime *= (1 - savings);
    }

    // Spritz adjustment (lid openings)
    if (cookDetails.spritz?.enabled && cookDetails.spritz.totalOpenings) {
      const openings = cookDetails.spritz.totalOpenings;
      const lidPenalty = openings * (smokerSpec.heatLoss.lidOpeningHeatLoss / 60); // minutes to hours
      cookTime += lidPenalty;
    }

    // Pan adjustment — pans block radiant heat from below and restrict convection airflow
    let panFactor = 1.0;
    if (cookDetails.pan?.enabled) {
      const panType = cookDetails.pan.type || 'aluminum-disposable';
      const elevated = cookDetails.pan.elevated || false;
      
      // Base pan factors by material type (from config)
      const panBaseFactor = constants.panFactors.baseMaterialFactors[panType] || 
        constants.panFactors.baseMaterialFactors['aluminum-disposable'];
      
      panFactor = panBaseFactor;
      
      // Elevated rack inside pan restores some under-meat convection
      if (elevated) {
        panFactor = 1.0 + (panFactor - 1.0) * (1 - constants.panFactors.elevationReduction);
      }
      
      // Compounding effect: pans + crowded spacing = worse than either alone
      // Pans act as baffles in the smoker, further disrupting airflow
      const spacingVal = cookDetails.spacing?.spacingFactor || 1.0;
      if (spacingVal > 1.0) {
        const crowdingCompound = 1.0 + ((spacingVal - 1.0) * constants.panFactors.crowdingCompound);
        panFactor *= crowdingCompound;
      }
      
      cookTime *= panFactor;
    }
    
    // NOW calculate phases from the FINAL adjusted cook time (not base time!)
    const phases = this.calculatePhases(cookTime, meatProps, cookDetails);

    const totalTime = cookTime + cookDetails.restHours;

    // Calculate timeline - work backward from target serving time if provided
    const targetDate = cookDetails.targetServingTime 
      ? new Date(cookDetails.targetServingTime)
      : new Date(Date.now() + 24 * 60 * 60 * 1000); // Default to 24 hours from now
    
    const startTime = new Date(targetDate.getTime() - totalTime * 60 * 60 * 1000);

    // Per-piece timelines
    const perPiece = this.calculatePerPiece(
      validPieces,
      baseCookRate,
      spacingFactor,
      thermalLoadFactor,
      weatherFactor,
      panFactor,
      startTime,
      cookDetails.restHours
    );

    // Generate recommended schedule with safety adjustments
    const recommendedSchedule = this.calculateRecommendedSchedule(perPiece, startTime);

    // Confidence assessment
    const confidence = this.assessConfidence(weatherFactor, spacingFactor, panFactor);
    
    // Calculate adjustments for breakdown
    const strategyAdjustment = baseTime * (spacingFactor * thermalLoadFactor * panFactor - 1);
    const weatherAdjustment = (baseTime * spacingFactor * thermalLoadFactor * panFactor) * (weatherFactor - 1);
    
    // Generate warnings and recommendations
    const warnings = this.generateWarnings(cookDetails, weather, cookTime, numPieces, spacingFactor, largestPiece);
    const recommendations = this.generateRecommendations(
      cookDetails, 
      smokerSpec, 
      meatProps, 
      numPieces, 
      largestPiece,
      spacingFactor,
      cookTime
    );
    
    // Calculate holding window with extended rest and warm hold buffers
    const holdingWindow = this.calculateHoldingWindow(
      cookDetails,
      cookTime,
      targetDate,
      startTime
    );
    
    // Generate input hash for reproducibility
    const inputHash = this.hashInputs(cookDetails, smokerSpec, meatProps, weather);

    return {
      calculation: {
        timestamp: new Date().toISOString(),
        inputHash,
        version: '2.3.0' // Web calculator with comprehensive reporting
      },
      phases: {
        heatUp: {
          durationHours: parseFloat(phases.heatUp.toFixed(2)),
          explanation: `Meat heats from ${meatProps.temperatures.startTempF}°F to ${meatProps.temperatures.stallStartF || cookDetails.targetTempF}°F`
        },
        stall: {
          durationHours: parseFloat(phases.stall.toFixed(2)),
          explanation: phases.stall > 0 
            ? `The stall (evaporative cooling plateau) from ${meatProps.temperatures.stallStartF}°F to ${meatProps.temperatures.stallEndF}°F`
            : 'No stall phase for this meat type'
        },
        postStall: {
          durationHours: parseFloat(phases.postStall.toFixed(2)),
          explanation: phases.stall > 0
            ? `Final push from ${meatProps.temperatures.stallEndF}°F to target ${cookDetails.targetTempF}°F`
            : `Cooking to target ${cookDetails.targetTempF}°F`
        },
        rest: {
          durationHours: cookDetails.restHours,
          explanation: `Rest period after cooking (wrapped in cooler): ${cookDetails.restHours} hours`
        }
      },
      summary: {
        totalCookTimeHours: parseFloat(cookTime.toFixed(2)),
        totalTimeIncludingRestHours: parseFloat(totalTime.toFixed(2)),
        recommendedStartTime: startTime.toISOString(),
        expectedCompletionTime: targetDate.toISOString(),
        baselineHours: parseFloat(baseTime.toFixed(2)),
        weatherAdjustmentHours: parseFloat(weatherAdjustment.toFixed(2)),
        strategyAdjustmentHours: parseFloat(strategyAdjustment.toFixed(2)),
        confidenceLevel: confidence,
        uncertaintyRangeHours: confidence === 'HIGH' ? 0.5 : confidence === 'MEDIUM' ? 1.0 : 2.0
      },
      timeline: {
        startTime,
        endTime: targetDate,
      },
      factors: {
        smokerEfficiency: this.calculateSmokerEfficiency(smokerSpec),
        weatherImpact: weatherFactor,
        windImpact: cookDetails.exposure.useWeatherWind ? 
          1.0 + (weather.current.windMph * smokerSpec.heatLoss.windFactor / 100) : 1.0,
        precipitationImpact: cookDetails.exposure.useWeatherPrecipitation && weather.current.precipitation ? 1.02 : 1.0,
        spacingFactor,
        thermalLoadFactor,
        panFactor
      },
      perPiece,
      recommendedSchedule,
      holdingWindow,
      recommendations,
      warnings
    };
  }

  /**
   * Get base cook rate for temperature
   */
  private getBaseCookRate(meatProps: MeatProperties, tempF: number): number {
    const empirical = meatProps.empirical;
    
    if (tempF <= 225) return empirical.baseCookRateHrsPerLb225F;
    if (tempF <= 250 && empirical.cookRateHrsPerLbAt250F) {
      return this.interpolate(225, empirical.baseCookRateHrsPerLb225F, 250, empirical.cookRateHrsPerLbAt250F, tempF);
    }
    if (tempF <= 275 && empirical.cookRateHrsPerLbAt275F) {
      return this.interpolate(250, empirical.cookRateHrsPerLbAt250F || empirical.baseCookRateHrsPerLb225F, 275, empirical.cookRateHrsPerLbAt275F, tempF);
    }

    // Extrapolate for higher temps
    return empirical.baseCookRateHrsPerLb225F * (225 / tempF) * 0.9;
  }

  /**
   * Calculate cooking phases
   */
  private calculatePhases(cookTime: number, meatProps: MeatProperties, _cookDetails: CookDetails) {
    const temps = meatProps.temperatures;
    
    // Simple percentage-based phase distribution (matches backend PoC)
    const heatupPercent = 0.50;    // ~50% of cook is heat-up
    const stallPercent = 0.25;     // ~25% is stall
    const postStallPercent = 0.25; // ~25% is post-stall

    // If there's a stall
    if (temps.stallStartF && temps.stallEndF) {
      return {
        heatUp: parseFloat((cookTime * heatupPercent).toFixed(2)),
        stall: parseFloat((cookTime * stallPercent).toFixed(2)),
        postStall: parseFloat((cookTime * postStallPercent).toFixed(2)),
      };
    }

    // No stall (chicken, etc) - distribute differently
    return {
      heatUp: parseFloat((cookTime * 0.7).toFixed(2)),
      stall: 0,
      postStall: parseFloat((cookTime * 0.3).toFixed(2)),
    };
  }

  /**
   * Calculate weather impact using forecast data during cook window if available
   * Uses smoker's actual thermal properties (baseCoefficient, windFactor, construction)
   */
  private calculateWeatherImpact(
    weather: WeatherData,
    smokerSpec: SmokerSpec,
    cookDetails: CookDetails,
    startTime?: Date,
    endTime?: Date
  ): number {
    // If we have hourly forecast and cook times, analyze the forecast window
    if (weather.hourly && weather.hourly.length > 0 && startTime && endTime) {
      // Filter hourly data to cook window
      const cookWindowForecast = weather.hourly.filter(hour => {
        const hourTime = new Date(hour.time);
        return hourTime >= startTime && hourTime <= endTime;
      });
      
      if (cookWindowForecast.length > 0) {
        // Calculate average/worst conditions during cook
        const minTemp = Math.min(...cookWindowForecast.map(h => h.temperatureF));
        const maxWind = Math.max(...cookWindowForecast.map(h => h.windMph));
        const precipHours = cookWindowForecast.filter(h => h.precipitation).length;
        const precipProbability = precipHours / cookWindowForecast.length;
        
        return this.calculateThermalImpact(
          minTemp,
          maxWind,
          precipProbability > 0.3,
          smokerSpec,
          cookDetails
        );
      }
    }

    // Fallback to current conditions if no hourly forecast or cook window
    return this.calculateThermalImpact(
      weather.current.temperatureF,
      weather.current.windMph,
      weather.current.precipitation,
      smokerSpec,
      cookDetails
    );
  }

  /**
   * Calculate thermal impact based on smoker's actual construction properties
   */
  private calculateThermalImpact(
    ambientTempF: number,
    windMph: number,
    hasPrecipitation: boolean,
    smokerSpec: SmokerSpec,
    cookDetails: CookDetails
  ): number {
    let factor = 1.0;
    const smokerTempF = cookDetails.smokerTempF;
    
    // Temperature differential impact (uses smoker's baseCoefficient)
    // Heat loss proportional to temp differential and insulation quality
    // baseCoefficient: BFG 2500 = 15, cheap single-wall = 40-50
    if (cookDetails.exposure.useWeatherTemp && ambientTempF < 70) {
      const tempDiff = smokerTempF - ambientTempF; // e.g., 250°F - 30°F = 220°F
      const normalizedDiff = tempDiff / 200; // Normalize around 200°F diff at 250°F smoker / 50°F ambient
      const baseImpact = (smokerSpec.heatLoss.baseCoefficient / 100) * normalizedDiff;
      factor += baseImpact;
    }

    // Wind impact (uses smoker's windFactor)
    // windFactor: BFG 2500 = 2.5, cheap smoker = 4-6
    // Lid seal quality affects wind penetration
    if (cookDetails.exposure.useWeatherWind && windMph > 5) {
      const sealMultiplier = smokerSpec.heatLoss.lidSealQuality === 'excellent' ? 0.7 :
                            smokerSpec.heatLoss.lidSealQuality === 'good' ? 0.85 : 1.0;
      const windImpact = (smokerSpec.heatLoss.windFactor * windMph * sealMultiplier) / 100;
      factor += windImpact;
    }

    // Precipitation adds evaporative cooling (universal ~2% penalty)
    if (cookDetails.exposure.useWeatherPrecipitation && hasPrecipitation) {
      factor *= 1.02;
    }

    // Double-wall construction provides additional protection
    if (smokerSpec.thermal.construction === 'double-wall') {
      // Reduce total weather penalty by 25% for double-wall
      factor = 1.0 + (factor - 1.0) * 0.75;
    }

    return factor;
  }

  /**
   * Calculate per-piece finish times
   */
  private calculatePerPiece(
    pieces: Array<{ id: string; weight: number }>,
    baseCookRate: number,
    spacingFactor: number,
    thermalLoadFactor: number,
    weatherFactor: number,
    panFactor: number,
    startTime: Date,
    restHours: number
  ) {
    const largestWeight = Math.max(...pieces.map(p => p.weight));
    const largestTime = largestWeight * baseCookRate * spacingFactor * thermalLoadFactor * panFactor * weatherFactor;
    const largestReadyTime = largestTime + restHours; // Largest piece's ready time (cook + rest)
    
    return pieces.map(piece => {
      // Smaller pieces finish proportionally faster
      const pieceFactor = piece.weight / largestWeight;
      const pieceTime = piece.weight * baseCookRate * spacingFactor * thermalLoadFactor * panFactor * weatherFactor * pieceFactor;
      const finishTime = new Date(startTime.getTime() + pieceTime * 60 * 60 * 1000);
      
      // After cooking, all pieces rest for the same duration
      const readyTime = new Date(finishTime.getTime() + restHours * 60 * 60 * 1000);
      
      // Calculate hold time from when this piece is ready until serving time
      // (This is extended rest or warm hold time until all pieces are ready)
      const pieceReadyTime = pieceTime + restHours;
      const holdHours = Math.max(0, largestReadyTime - pieceReadyTime);
      const finishesBeforeLargest = piece.weight < largestWeight;

      return {
        id: piece.id,
        weight: piece.weight,
        finishTime,
        readyTime,
        restHours: parseFloat(restHours.toFixed(1)),
        holdHours: parseFloat(holdHours.toFixed(1)),
        cookTimeHours: parseFloat(pieceTime.toFixed(1)),
        finishesBeforeLargest,
      };
    }).sort((a, b) => a.readyTime.getTime() - b.readyTime.getTime());
  }

  /**
   * Calculate recommended schedule with safety adjustments
   * Max rest: 2.5h (extended cooler rest)
   * Max warm hold: 2h (FDA guideline)
   */
  private calculateRecommendedSchedule(
    currentSchedule: Array<{
      id: string;
      weight: number;
      finishTime: Date;
      readyTime: Date;
      restHours: number;
      holdHours: number;
      cookTimeHours: number;
      finishesBeforeLargest: boolean;
    }>,
    _startTime: Date
  ) {
    const MAX_REST_HOURS = 2.5;
    const MAX_SAFE_HOLD_HOURS = 2.0; // FDA guideline for warm holding cooked food

    // Find the target ready time (when largest piece is ready)
    const latestReadyTime = Math.max(...currentSchedule.map(p => p.readyTime.getTime()));

    return currentSchedule.map(piece => {
      const timeDiff = (latestReadyTime - piece.readyTime.getTime()) / (60 * 60 * 1000);
      
      // If piece would be holding >2h with current rest time, extend rest
      if (timeDiff > MAX_SAFE_HOLD_HOURS) {
        // Calculate how much extra rest time we need
        const neededRestExtension = Math.min(timeDiff - MAX_SAFE_HOLD_HOURS, MAX_REST_HOURS - piece.restHours);
        const adjustedRestHours = piece.restHours + neededRestExtension;
        const adjustedReadyTime = new Date(piece.finishTime.getTime() + adjustedRestHours * 60 * 60 * 1000);
        const adjustedHoldHours = Math.max(0, (latestReadyTime - adjustedReadyTime.getTime()) / (60 * 60 * 1000));
        
        let adjustmentReason = '';
        if (adjustedRestHours >= MAX_REST_HOURS && adjustedHoldHours > MAX_SAFE_HOLD_HOURS) {
          adjustmentReason = `Extended rest to maximum ${MAX_REST_HOURS}h, but still ${adjustedHoldHours.toFixed(1)}h hold needed - consider serving earlier or using warming equipment`;
        } else {
          adjustmentReason = `Extended rest from ${piece.restHours}h to ${adjustedRestHours.toFixed(1)}h to reduce warm hold time`;
        }

        return {
          ...piece,
          readyTime: adjustedReadyTime,
          restHours: parseFloat(adjustedRestHours.toFixed(1)),
          holdHours: parseFloat(adjustedHoldHours.toFixed(1)),
          adjustmentReason
        };
      }

      // No adjustment needed
      return { ...piece };
    }).sort((a, b) => a.readyTime.getTime() - b.readyTime.getTime());
  }

  /**
   * Assess confidence level
   */
  private assessConfidence(weatherFactor: number, spacingFactor: number, panFactor: number = 1.0): 'HIGH' | 'MEDIUM' | 'LOW' {
    if (weatherFactor > 1.25) {
      return 'LOW';
    }
    if (weatherFactor > 1.15 || spacingFactor > 1.2 || panFactor > 1.10) {
      return 'MEDIUM';
    }
    return 'HIGH';
  }

  /**
   * Calculate smoker efficiency based on thermal properties
   */
  private calculateSmokerEfficiency(smokerSpec: SmokerSpec): number {
    // Base on temperature stability and construction
    let efficiency = 0.85; // baseline
    
    // Temperature stability (tighter control = higher efficiency)
    if (smokerSpec.thermal.temperatureStabilityDegrees <= 5) {
      efficiency = 0.95; // Excellent (like BFG 2500)
    } else if (smokerSpec.thermal.temperatureStabilityDegrees <= 10) {
      efficiency = 0.90; // Good
    }
    
    // Bonus for double-wall construction
    if (smokerSpec.thermal.construction === 'double-wall') {
      efficiency += 0.03;
    }
    
    // Bonus for low baseCoefficient (excellent insulation)
    if (smokerSpec.heatLoss.baseCoefficient <= 15) {
      efficiency += 0.02;
    }
    
    return Math.min(efficiency, 0.98); // Cap at 98%
  }

  /**
   * Linear interpolation
   */
  private interpolate(x1: number, y1: number, x2: number, y2: number, x: number): number {
    return y1 + ((x - x1) / (x2 - x1)) * (y2 - y1);
  }
  
  /**
   * Generate warnings for risky conditions
   */
  private generateWarnings(
    cookDetails: CookDetails,
    weather: WeatherData,
    cookTime: number,
    _numPieces: number, // Prefix with _ to indicate intentionally unused
    spacingFactor: number,
    _largestPiece: number // Prefix with _ to indicate intentionally unused
  ): string[] {
    const warnings: string[] = [];
    
    // Extreme cold
    if (weather.current.temperatureF < 25) {
      warnings.push('⚠️ EXTREME COLD: Temperature control may be difficult, cook time highly variable');
    }
    
    // High wind
    if (weather.current.windMph > 20) {
      warnings.push('⚠️ HIGH WIND: Significant heat loss expected, strongly recommend windbreak');
    }
    
    // Heavy precipitation (if hourly data available)
    if (weather.hourly) {
      const precipHours = weather.hourly.filter(h => h.precipitation).length;
      if (precipHours / weather.hourly.length > 0.5) {
        warnings.push('⚠️ HEAVY PRECIPITATION: Smoker protection critical, temperature drops likely');
      }
    } else if (weather.current.precipitation) {
      warnings.push('⚠️ PRECIPITATION: Smoker protection recommended');
    }
    
    // Fully exposed in bad weather
    if (cookDetails.exposure.useWeatherTemp && cookDetails.exposure.useWeatherWind &&
        (weather.current.temperatureF < 35 || weather.current.windMph > 15)) {
      warnings.push('⚠️ EXPOSED SMOKER: Cold + wind will add time - consider shelter');
    }
    
    // Long cook
    if (cookTime > 18) {
      warnings.push('⚠️ LONG COOK: Plan for overnight monitoring or start earlier');
    }
    
    // Crowded smoker
    if (spacingFactor >= 2.0) {
      warnings.push('⚠️ CROWDED COOK: Very tight spacing will significantly extend cook time');
    }
    
    // Pan + crowded compounding
    if (cookDetails.pan?.enabled && spacingFactor >= 1.5) {
      warnings.push('⚠️ PANS + CROWDED: Aluminum pans compound crowding — pans block airflow further. Expect significantly longer cook.');
    }
    
    // Pan warning
    if (cookDetails.pan?.enabled && !cookDetails.pan.elevated) {
      warnings.push('⚠️ PAN WITHOUT RACK: Meat sitting directly in pan juices caps bottom surface temp at ~212°F. Consider a rack insert inside the pan.');
    }
    
    // Beef ribs info message (not really a warning, just informational)
    if (cookDetails.meatType === 'beef-ribs') {
      warnings.push('ℹ️ BEEF RIBS: Cook time is based on thickness (~9 hrs at 250°F), not weight. Multiple ribs cook in parallel. Weight input is used only for thermal load adjustments with multiple pieces.');
    }
    
    // No target serving time
    if (!cookDetails.targetServingTime) {
      warnings.push('⚠️ NO TARGET TIME: Set a target serving time for accurate planning');
    }
    
    return warnings;
  }
  
  /**
   * Generate recommendations
   */
  private generateRecommendations(
    cookDetails: CookDetails,
    _smokerSpec: SmokerSpec,
    _meatProps: MeatProperties, // Prefix with _ to indicate intentionally unused
    numPieces: number,
    largestPiece: number,
    _spacingFactor: number,
    _cookTime: number
  ): string[] {
    const recs: string[] = [];
    
    // Multi-piece planning - actionable recommendation
    if (numPieces > 1) {
      recs.push(`${numPieces} pieces: Cook time based on largest piece (${largestPiece.toFixed(1)} lbs), smaller pieces will finish earlier`);
    }
    
    // Wrapping strategy - actionable advice
    if (!cookDetails.wrapping?.enabled) {
      recs.push('Naked cook: Better bark, but slightly longer cook time - plan accordingly');
    } else if (cookDetails.wrapping.wrapType === 'butcher-paper') {
      recs.push('Butcher paper wrap: Good balance of bark development and time savings');
    }
    
    // Temperature flexibility - actionable recommendation
    recs.push(`Target temp: ${cookDetails.targetTempF}°F - adjust based on probe feel (probe should slide in like butter)`);
    
    // Weather protection - actionable recommendation
    if (cookDetails.exposure.useWeatherTemp || cookDetails.exposure.useWeatherWind) {
      recs.push('🌡️ Weather protection recommended: windbreak and/or shelter will improve consistency');
    }

    // Pan usage - actionable recommendation
    if (cookDetails.pan?.enabled) {
      if (!cookDetails.pan.elevated) {
        recs.push('🍳 Pan tip: Place a small cooling rack inside each pan to lift meat above drippings — restores airflow underneath and can save 30-45 min');
      }
      recs.push('🍳 Pan factor applied: Pans block radiant heat and restrict convection on the bottom of the meat, adding ~10-15% to cook time');
    }
    
    return recs;
  }
  
  /**
   * Calculate holding window flexibility
   */
  private calculateHoldingWindow(
    cookDetails: CookDetails,
    cookTime: number,
    targetDate: Date,
    _recommendedStart: Date
  ): {
    earliestSafeStart: string;
    recommendedStart: string;
    latestSafeStart: string;
    totalFlexibilityHours: number;
    explanation: string;
  } | undefined {
    // Only calculate if we have target serving time
    if (!cookDetails.targetServingTime) {
      return undefined;
    }
    
    // Holding buffers: extended cooler rest + warm hold
    const maxExtendedRestHours = 4;
    const maxWarmHoldHours = 2;
    const totalHoldingBuffer = maxExtendedRestHours + maxWarmHoldHours;
    
    const totalTimeNeeded = cookTime + cookDetails.restHours;
    
    // Earliest: Start early and use full holding buffers
    const earliestMs = targetDate.getTime() - ((totalTimeNeeded + totalHoldingBuffer) * 60 * 60 * 1000);
    const earliestStart = new Date(earliestMs);
    
    // Latest: Bare minimum time (no holding buffer)
    const latestMs = targetDate.getTime() - (totalTimeNeeded * 60 * 60 * 1000);
    const latestStart = new Date(latestMs);
    
    // Recommended: Split the difference (use half the buffer)
    const recommendedMs = targetDate.getTime() - ((totalTimeNeeded + (totalHoldingBuffer / 2)) * 60 * 60 * 1000);
    const recommendedStartCalc = new Date(recommendedMs);
    
    return {
      earliestSafeStart: earliestStart.toISOString(),
      recommendedStart: recommendedStartCalc.toISOString(),
      latestSafeStart: latestStart.toISOString(),
      totalFlexibilityHours: parseFloat(totalHoldingBuffer.toFixed(2)),
      explanation: `You have ${totalHoldingBuffer} hours of flexibility (${maxExtendedRestHours}h extended rest + ${maxWarmHoldHours}h warm hold). Pieces can rest wrapped in cooler, then hold warm until serving.`
    };
  }
  
  /**
   * Hash inputs for reproducibility tracking
   */
  private hashInputs(
    cookDetails: CookDetails,
    smokerSpec: SmokerSpec,
    meatProps: MeatProperties,
    weather: WeatherData
  ): string {
    const validPieces = cookDetails.pieces.filter(p => p.weight > 0);
    const largestPiece = Math.max(...validPieces.map(p => p.weight));
    
    // Simple string-based hash (not cryptographic, just for tracking)
    const inputString = JSON.stringify({
      largestPieceWeight: largestPiece,
      numPieces: validPieces.length,
      spacingFactor: cookDetails.spacing?.spacingFactor,
      smokerModel: smokerSpec.model,
      setTemp: cookDetails.smokerTempF,
      targetTemp: cookDetails.targetTempF,
      meatType: meatProps.displayName,
      ambientTemp: weather.current.temperatureF.toFixed(0),
      wind: weather.current.windMph.toFixed(0),
      wrapped: cookDetails.wrapping?.enabled
    });
    
    // Simple hash using string manipulation (good enough for tracking)
    let hash = 0;
    for (let i = 0; i < inputString.length; i++) {
      const char = inputString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(16).substring(0, 12);
  }
}
